/**
 * The evening wrapped as a video for a story: the story cards, one after another, 1080×1920, five
 * seconds each, silent (the guest adds their own music in the story app). Made on the guest's own
 * phone with nothing but the browser — a canvas, `captureStream` and `MediaRecorder` — and never
 * uploaded anywhere: it goes to the phone's share sheet, or downloads.
 *
 * The motion is gentle: each card drifts slowly closer, and the next slides up over it as the last one
 * settles back and dims. Energy from motion, never from brightness: no flash, no strobe, nothing that
 * pulses. The only light change is the fade up from dark at the start and back down at the end.
 *
 * The recording takes as long as the video does, so the screen must stay on: the phone is asked to keep
 * it awake, and if the page is hidden anyway the recording stops cleanly and can be tried again.
 */

import { STORY } from './storyClock.js';

export const VIDEO = {
  W: 1080,
  H: 1920,
  FPS: 30,
  /** Each slide, as on the phone (storyClock.ts `STORY.SLIDE_MS`): the video is slides × 5 s. */
  SLIDE_S: STORY.SLIDE_MS / 1000,
  /** The next card sliding up, at the start of its five seconds. */
  MOVE_S: 0.8,
  /** Up from dark at the start, back down at the end: slow enough never to read as a flash. */
  IN_S: 0.9,
  OUT_S: 0.9,
  /** How much closer a card drifts while it is up. */
  DRIFT: 0.045,
  /** Plenty for slides of still words at 1080×1920: about half a megabyte a second, 25 MB for ten slides. */
  BITS_PER_SECOND: 4_000_000,
} as const;

/**
 * The containers to ask for, best first: MP4 (H.264) is what the story apps take, and what an iPhone and
 * a recent Android Chrome record; WebM is the fallback for the rest.
 */
export const VIDEO_TYPES = [
  'video/mp4;codecs=avc1',
  'video/mp4',
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
] as const;

/** The part of `MediaRecorder` the choice needs, so a test can stand one in. */
export interface RecorderLike {
  isTypeSupported?: (type: string) => boolean;
}

/**
 * Every type of `VIDEO_TYPES` the phone says it can record, best first, skipping any in `skip`. `['']`
 * lets the phone choose (a recorder too old to be asked); empty when it can record none of them.
 */
export function videoTypes(recorder: RecorderLike | null | undefined, skip: readonly string[] = []): string[] {
  if (!recorder) return [];
  const ask = recorder.isTypeSupported;
  if (typeof ask !== 'function') return [''];
  return VIDEO_TYPES.filter((type) => {
    if (skip.includes(type)) return false;
    try {
      return ask.call(recorder, type) === true;
    } catch {
      // A browser that throws on a type it does not know simply cannot record it.
      return false;
    }
  });
}

/** The type to record in: the first the phone can (`videoTypes`), `''` to let it choose, null for none. */
export function pickVideoType(recorder: RecorderLike | null | undefined, skip: readonly string[] = []): string | null {
  return videoTypes(recorder, skip)[0] ?? null;
}

/** What a phone needs to make the video at all, read off its globals (stubbed in a test). */
export interface VideoEnv {
  MediaRecorder?: unknown;
  HTMLCanvasElement?: { prototype: object };
}

/** Whether this phone can make the video: a recorder, a canvas it can film, and a type it can record. */
export function videoSupported(env: VideoEnv | undefined = typeof window === 'undefined' ? undefined : (window as unknown as VideoEnv)): boolean {
  if (!env) return false;
  const recorder = env.MediaRecorder;
  if (typeof recorder !== 'function') return false;
  const canvas = env.HTMLCanvasElement?.prototype as { captureStream?: unknown } | undefined;
  if (typeof canvas?.captureStream !== 'function') return false;
  return pickVideoType(recorder as RecorderLike) !== null;
}

/** The file's own type and ending, without the codecs the recorder was asked for. */
export function videoKind(mimeType: string): { type: 'video/mp4' | 'video/webm'; ext: 'mp4' | 'webm' } {
  return /mp4|quicktime/i.test(mimeType) ? { type: 'video/mp4', ext: 'mp4' } : { type: 'video/webm', ext: 'webm' };
}

/** The finished video as a file to share or save: a name with no date in it, and nothing else attached. */
export function videoFile(blob: Blob, mimeType: string, base: string): File {
  const { type, ext } = videoKind(mimeType || blob.type);
  return new File([blob], `${base}.${ext}`, { type });
}

// ---------------------------------------------------------------------------
// The timeline: pure, so a test can walk through it.

export interface VideoPlan {
  cards: number;
  /** Seconds each card holds the screen, its rise-in included. */
  slot: number;
  total: number;
}

export function videoPlan(cards: number): VideoPlan {
  const n = Math.max(1, Math.floor(cards));
  return { cards: n, slot: VIDEO.SLIDE_S, total: n * VIDEO.SLIDE_S };
}

/** One card on one frame: drawn at `alpha`, `scale` about the centre, raised by `dy` px, darkened by `dim`. */
export interface Layer {
  index: number;
  alpha: number;
  scale: number;
  dy: number;
  dim: number;
}

export interface Frame {
  /** Bottom first. */
  layers: Layer[];
  /** Dark over everything: 1 at the very start and end, 0 in between. */
  veil: number;
}

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
/** Slow out of rest, slow into rest: nothing jumps. */
export const ease = (x: number) => {
  const t = clamp01(x);
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
};

/** What is on screen `t` seconds in. */
export function frameAt(t: number, plan: VideoPlan): Frame {
  const time = Math.min(Math.max(0, t), plan.total);
  const i = Math.min(plan.cards - 1, Math.floor(time / plan.slot));
  const u = time - i * plan.slot;
  // A card drifts closer from the moment it starts to rise in until it has gone under the next.
  const drift = (k: number, at: number) => 1 + VIDEO.DRIFT * clamp01((at - k * plan.slot) / (plan.slot + VIDEO.MOVE_S));
  const layers: Layer[] = [];
  if (i > 0 && u < VIDEO.MOVE_S) {
    // The next card slides up over the last, whole and opaque, so their words never overlap; the last is
    // pushed up a little behind it, settling back and dimming.
    const p = ease(u / VIDEO.MOVE_S);
    layers.push({ index: i - 1, alpha: 1, scale: drift(i - 1, time) * (1 - 0.06 * p), dy: -0.18 * VIDEO.H * p, dim: 0.7 * p });
    layers.push({ index: i, alpha: 1, scale: drift(i, time), dy: VIDEO.H * (1 - p), dim: 0 });
  } else {
    layers.push({ index: i, alpha: 1, scale: drift(i, time), dy: 0, dim: 0 });
  }
  const veil = Math.max(1 - ease(time / VIDEO.IN_S), ease((time - (plan.total - VIDEO.OUT_S)) / VIDEO.OUT_S));
  return { layers, veil: Math.round(veil * 1000) / 1000 };
}

// ---------------------------------------------------------------------------
// Drawing and recording, on the phone.

const GROUND = '#06060a';

/** A few faint stars behind the cards, drifting up: seen only round a card as it settles back. */
function starfield(count: number): { x: number; y: number; r: number; a: number; v: number }[] {
  let seed = 7;
  const next = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  return Array.from({ length: count }, () => ({
    x: next() * VIDEO.W,
    y: next() * VIDEO.H,
    r: 1.2 + next() * 2,
    a: 0.12 + next() * 0.3,
    v: 8 + next() * 22,
  }));
}

const STARS = starfield(90);

export function drawFrame(ctx: CanvasRenderingContext2D, images: readonly CanvasImageSource[], plan: VideoPlan, t: number): void {
  const { W, H } = VIDEO;
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.fillStyle = GROUND;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = '#d8d2ec';
  for (const star of STARS) {
    const y = (((star.y - star.v * t) % H) + H) % H;
    ctx.globalAlpha = star.a;
    ctx.beginPath();
    ctx.arc(star.x, y, star.r, 0, Math.PI * 2);
    ctx.fill();
  }
  const frame = frameAt(t, plan);
  for (const layer of frame.layers) {
    const image = images[layer.index];
    if (!image) continue;
    ctx.save();
    ctx.globalAlpha = layer.alpha;
    ctx.translate(W / 2, H / 2 + layer.dy);
    ctx.scale(layer.scale, layer.scale);
    if (layer.dy > 0) {
      // A soft shadow along the top of a card sliding in, so it reads as a card laid on the last.
      const shade = ctx.createLinearGradient(0, -H / 2 - 140, 0, -H / 2);
      shade.addColorStop(0, 'rgba(0,0,0,0)');
      shade.addColorStop(1, 'rgba(0,0,0,0.55)');
      ctx.fillStyle = shade;
      ctx.fillRect(-W / 2, -H / 2 - 140, W, 140);
    }
    ctx.drawImage(image, -W / 2, -H / 2, W, H);
    if (layer.dim > 0) {
      ctx.fillStyle = `rgba(0,0,0,${layer.dim})`;
      ctx.fillRect(-W / 2, -H / 2, W, H);
    }
    ctx.restore();
  }
  if (frame.veil > 0) {
    ctx.globalAlpha = frame.veil;
    ctx.fillStyle = GROUND;
    ctx.fillRect(0, 0, W, H);
  }
  ctx.restore();
}

/** A card's picture, ready to draw many times a second. */
export interface Loaded {
  source: CanvasImageSource;
  close: () => void;
}

export async function loadImage(blob: Blob): Promise<Loaded> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob);
      return { source: bitmap, close: () => bitmap.close() };
    } catch {
      // An older Safari decodes a picture from a blob only through an <img>.
    }
  }
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.src = url;
  try {
    await img.decode();
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
  return { source: img, close: () => URL.revokeObjectURL(url) };
}

/** Why a recording ended without a video. */
export class VideoStopped extends Error {
  constructor(readonly why: 'aborted' | 'unsupported' | 'failed') {
    super(`video ${why}`);
    this.name = 'VideoStopped';
  }
}

export interface RecordOptions {
  /** On the page while it records: a canvas off the page is not filmed by every phone. */
  canvas: HTMLCanvasElement;
  images: readonly CanvasImageSource[];
  /** From `videoTypes`, best first: the first the recorder starts with is used. `''` lets the phone choose. */
  mimeTypes: readonly string[];
  signal?: AbortSignal;
  /** Every frame: seconds done, and the video's length. */
  onTick?: (elapsed: number, total: number) => void;
}

/**
 * Plays the cards onto `canvas` in real time while a MediaRecorder films it, and resolves with the
 * video. Rejects with `VideoStopped` when it is aborted, cannot start, or comes out empty.
 */
export async function recordVideo(o: RecordOptions): Promise<{ blob: Blob; mimeType: string }> {
  const { canvas, images, signal } = o;
  if (signal?.aborted) throw new VideoStopped('aborted');
  canvas.width = VIDEO.W;
  canvas.height = VIDEO.H;
  const ctx = canvas.getContext('2d');
  if (!ctx || typeof canvas.captureStream !== 'function' || typeof MediaRecorder !== 'function') throw new VideoStopped('unsupported');
  const plan = videoPlan(images.length);
  drawFrame(ctx, images, plan, 0);

  const stream = canvas.captureStream(VIDEO.FPS);
  const stopTracks = () => stream.getTracks().forEach((track) => track.stop());
  const chunks: Blob[] = [];
  let failed = false;
  let done: () => void = () => undefined;
  const stopped = new Promise<void>((resolve) => (done = resolve));

  // The first type the recorder will start with: a phone can say yes to a type and still refuse it.
  let recorder: MediaRecorder | null = null;
  let used = '';
  for (const type of o.mimeTypes.length > 0 ? o.mimeTypes : ['']) {
    try {
      const r = new MediaRecorder(
        stream,
        type ? { mimeType: type, videoBitsPerSecond: VIDEO.BITS_PER_SECOND } : { videoBitsPerSecond: VIDEO.BITS_PER_SECOND },
      );
      // Only the recorder that started is listened to: a refused one may still say something later.
      r.ondataavailable = (e) => {
        if (recorder === r && e.data && e.data.size > 0) chunks.push(e.data);
      };
      r.onstop = () => {
        if (recorder === r) done();
      };
      r.onerror = () => {
        if (recorder !== r) return;
        failed = true;
        done();
      };
      r.start();
      recorder = r;
      used = type;
      break;
    } catch {
      // Refused: the next type.
    }
  }
  if (!recorder) {
    stopTracks();
    throw new VideoStopped('unsupported');
  }
  const rec = recorder;

  let raf = 0;
  const halt = () => {
    cancelAnimationFrame(raf);
    if (rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch {
        // Already stopping.
      }
    }
  };

  try {
    await new Promise<void>((resolve, reject) => {
      const onAbort = () => {
        cancelAnimationFrame(raf);
        reject(new VideoStopped('aborted'));
      };
      signal?.addEventListener('abort', onAbort, { once: true });
      const t0 = performance.now();
      const tick = () => {
        if (failed) {
          signal?.removeEventListener('abort', onAbort);
          reject(new VideoStopped('failed'));
          return;
        }
        const t = (performance.now() - t0) / 1000;
        drawFrame(ctx, images, plan, Math.min(t, plan.total));
        o.onTick?.(Math.min(t, plan.total), plan.total);
        if (t >= plan.total) {
          signal?.removeEventListener('abort', onAbort);
          resolve();
          return;
        }
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    });
    // The last dark frame, held a moment so the recorder has it before it stops.
    await new Promise((r) => setTimeout(r, 200));
    halt();
    await stopped;
    if (failed) throw new VideoStopped('failed');
    const mimeType = rec.mimeType || used || chunks[0]?.type || 'video/webm';
    const blob = new Blob(chunks, { type: videoKind(mimeType).type });
    if (blob.size === 0) throw new VideoStopped('failed');
    return { blob, mimeType };
  } catch (err) {
    halt();
    throw err instanceof VideoStopped ? err : new VideoStopped('failed');
  } finally {
    stopTracks();
  }
}

/** Asks the phone to keep the screen on while it records; nothing happens on a phone that cannot. */
export async function keepAwake(): Promise<{ release: () => void }> {
  const nav = navigator as Navigator & { wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> } };
  try {
    const lock = await nav.wakeLock?.request('screen');
    if (lock) return { release: () => void lock.release().catch(() => undefined) };
  } catch {
    // Refused (low battery, or not allowed here): the guest is asked to keep the screen open instead.
  }
  return { release: () => undefined };
}

/** An iPhone or iPad, including an iPad that says it is a Mac. */
export function applePhone(userAgent: string, touchPoints: number): boolean {
  return /iPhone|iPad|iPod/i.test(userAgent) || (/Macintosh/i.test(userAgent) && touchPoints > 1);
}

/**
 * How the video reaches the camera roll. On an iPhone, the share sheet: its Save Video puts it in Photos.
 * Elsewhere a download (Android's Downloads, a computer's), unless the phone cannot download and can
 * share, as in some apps' own browsers.
 */
export function saveRoute(o: { userAgent: string; touchPoints: number; canShareFile: boolean; canDownload: boolean }): 'share' | 'download' {
  if (o.canShareFile && (applePhone(o.userAgent, o.touchPoints) || !o.canDownload)) return 'share';
  return 'download';
}
